// Escala del diseño, como en Bambu Studio: 1 = 100 %.
// El cálculo vive en `@laser/core` (`escalarAnalisis`); aquí sólo están los ayudantes
// del panel: leer la escala de una línea, sincronizar los campos % ↔ mm y escribirla
// en la solicitud que se manda a recotizar.
import { ESCALA_MAX, ESCALA_MIN, escalarAnalisis } from '@laser/core';
import type { Analisis, LineaSolicitud, SolicitudCotizacion } from '@laser/core';
import { numero } from './formato';

export { escalarAnalisis, ESCALA_MIN, ESCALA_MAX };

/** Ningún lado del diseño puede quedar por debajo de 1 mm. */
export const LADO_MIN_MM = 1;

export interface Escalas { escalaX: number; escalaY: number }
export interface Base { anchoMm: number; altoMm: number }
export type CampoEscala = 'escalaX' | 'escalaY' | 'anchoMm' | 'altoMm';

/** Escala utilizable: ausente o inválida = 1 (100 %); fuera de rango se acota (el core lanzaría error). */
const valida = (v: number | null | undefined): number => {
  if (v == null || !Number.isFinite(v) || v <= 0) return 1;
  return Math.min(ESCALA_MAX, Math.max(ESCALA_MIN, v));
};

const redondear = (n: number, decimales: number) => {
  const f = 10 ** decimales;
  return Math.round(n * f) / f;
};

/** Escalas de una línea de la solicitud, con 1 (100 %) por omisión. */
export const escalaDeLinea = (l?: Pick<LineaSolicitud, 'escalaX' | 'escalaY'> | null): Escalas =>
  ({ escalaX: valida(l?.escalaX), escalaY: valida(l?.escalaY) });

/** ¿Hay una escala distinta de 100 %? */
export const hayEscala = (e: Escalas): boolean => Math.abs(e.escalaX - 1) > 1e-9 || Math.abs(e.escalaY - 1) > 1e-9;

const pct = (v: number) => numero(redondear(v * 100, 2));

/** "Escala 150 %" o, si se estiró, "Escala 125 × 80 %". */
export const textoEscala = (e: Escalas): string =>
  `Escala ${e.escalaX === e.escalaY ? `${pct(e.escalaX)} %` : `${pct(e.escalaX)} × ${pct(e.escalaY)} %`}`;

/** Tamaño de un lado ya escalado, redondeado a 0.01 mm. */
export const mmDeEscala = (ladoMm: number, escala: number) => redondear(ladoMm * escala, 2);

/** Escala mínima y máxima de un lado: 1 %–10000 %, sin bajar de 1 mm. */
export function limitesEscala(ladoMm: number): { min: number; max: number } {
  const min = ladoMm > 0 ? Math.max(ESCALA_MIN, LADO_MIN_MM / ladoMm) : ESCALA_MIN;
  return { min, max: Math.max(min, ESCALA_MAX) };
}

const acotar = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/**
 * Sincroniza los cuatro campos del control de escala (% X, % Y, mm X, mm Y).
 * Con `uniforme`, cualquier campo mueve las dos escalas conservando la proporción;
 * sin él, sólo se estira el eje que se editó. Siempre dentro de los límites.
 */
export function aplicarCampoEscala(actual: Escalas, base: Base, campo: CampoEscala, valor: number, uniforme: boolean): Escalas {
  if (!Number.isFinite(valor) || valor <= 0) return actual;
  const ejeX = campo === 'escalaX' || campo === 'anchoMm';
  const ladoBase = ejeX ? base.anchoMm : base.altoMm;
  const cruda = campo === 'escalaX' || campo === 'escalaY' ? valor / 100 : (ladoBase > 0 ? valor / ladoBase : 1);
  if (!Number.isFinite(cruda) || cruda <= 0) return actual;

  const lx = limitesEscala(base.anchoMm);
  const ly = limitesEscala(base.altoMm);
  if (uniforme) {
    const e = redondear(acotar(cruda, Math.max(lx.min, ly.min), Math.min(lx.max, ly.max)), 6);
    return { escalaX: e, escalaY: e };
  }
  const lim = ejeX ? lx : ly;
  const e = redondear(acotar(cruda, lim.min, lim.max), 6);
  return ejeX ? { ...actual, escalaX: e } : { ...actual, escalaY: e };
}

/** La solicitud que se manda a recotizar siempre lleva escalaX/escalaY explícitas. */
export const conEscalas = (s: SolicitudCotizacion): SolicitudCotizacion => ({
  ...s,
  lineas: s.lineas.map((l) => ({ ...l, ...escalaDeLinea(l) })),
});

/** Análisis de cada archivo ya escalado según su línea de la solicitud. */
export function analisisEscalados<T extends { id: string; analisis: Analisis | null }>(
  archivos: T[], lineas: readonly LineaSolicitud[],
): Record<string, Analisis | null> {
  return Object.fromEntries(archivos.map((a) => {
    const e = escalaDeLinea(lineas.find((l) => l.archivoId === a.id));
    return [a.id, a.analisis ? escalarAnalisis(a.analisis, e.escalaX, e.escalaY) : a.analisis];
  }));
}
